import React, {useEffect, useState} from "react";
import {ChecklistObject, KeyTextString} from "./types";
import {Box, Button, Typography, useTheme} from "@mui/material";
import {FormGenContainer, FormGenContentItem, FormGenLayoutSource} from "./FormGenContainer";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "./StyledComponents";
import {AddOutlined, CheckBoxOutlineBlankOutlined, CheckBoxOutlined} from "@mui/icons-material";
import {DW_LG, PD_LG, PD_SM} from "./dimens";
import {DialogFragment, PageFragment, PageFragmentProps} from "./PageFragment";
import {BaseApp, DIALOG_FLAG_DISABLE_BACKDROP_CLICK, DIALOG_FLAG_SHOW_CLOSE} from "./BaseApp";
import {FormGen} from "./formgen";

export class EditChecklistHelper {

  static addChecklist(checklist: ChecklistObject) {
    BaseApp.CONTEXT.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK},
      () => <EditChecklistFragment edit={checklist} onChecklistSaved={checklist => BaseApp.CONTEXT.hideDialog()}/>);
  }

  static editChecklist(checklist: ChecklistObject) {
    BaseApp.CONTEXT.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_DISABLE_BACKDROP_CLICK},
      () => <EditChecklistFragment
        edit={checklist}
        onChecklistSaved={checklist => BaseApp.CONTEXT.hideDialog()}/>);
  }
}

type EditChecklistFragmentProps = PageFragmentProps & {
  add?: ChecklistObject,
  edit?: ChecklistObject,
  onChecklistSaved: (checklist: ChecklistObject) => void,
}

class EditChecklist {

  @FormGen({
    name: "Items",
    type: "array_string",
  })
  items: string[];
}

class EditChecklistFragment extends DialogFragment<EditChecklistFragmentProps> {

  private readonly checklist = this.props.edit ? this.props.edit : this.props.add;
  private readonly editChecklist = new EditChecklist();

  protected onCreateState() {
    return {
      ...super.onCreateState(),
      title: this.props.edit ? "Edit" : "Add",
    };
  }

  protected styleFlags(): number {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    return this.props.edit ? "Save" : "Add";
  }

  onActionButtonClicked() {
    this.addChecklist().then(() => this.props.onChecklistSaved(this.checklist));
  }

  private async addChecklist() {
    // await this.checklist.save();
  }

  renderContent(): React.ReactElement | null {
    return <Box style={{display: "flex", width: DW_LG}}>
      <FormGenContainer
        content={this.editChecklist}/>
    </Box>;
  }
}

class FormGenChecklistOption {

  selected?: boolean;

  constructor(readonly item: KeyTextString) {
  }
}

export function FormGenChecklist(props: {
  contentItem: FormGenContentItem,
  disabled?: boolean,
  index: number,
  source: FormGenLayoutSource,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [options, setOptions] = useState<FormGenChecklistOption[]>([]);
  const [reloadOptions, setReloadOptions] = useState(false);

  const metadata = props.contentItem.metadata;
  useEffect(() => {
    (async () => {
      const value: string[] = props.source.getValue(props.contentItem);
      const options: FormGenChecklistOption[] = (await metadata.checklistLoader(value))?.items.map(keytext => new FormGenChecklistOption(keytext));
      setOptions(options);
      setLoading(false);
      options?.forEach(option => option.selected = value?.includes(option.item.key));
    })();
  }, []);

  useEffect(() => {
    let active = true;
    if (!reloadOptions) {
      return;
    }
    setReloadOptions(false);
    setLoading(true);
    (async () => {
      const value = props.source.getValue(props.contentItem);
      const options: FormGenChecklistOption[] = [];
      options.push(...(await metadata.checklistLoader(value, metadata.checklistName))?.items.map(keytext => new FormGenChecklistOption(keytext)));
      // if (currentOption) {
      //   options.push(currentOption);
      // }
      if (active) {
        setOptions(options);
        setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [reloadOptions]);

  const multiple = metadata.checklistMulti;
  return <StyledBoxColumn style={{border: "1px solid " + theme.palette.primary.main, padding: PD_SM}}>
    {metadata.checklistCreate || metadata.checklistEdit || multiple
      ? <StyledBoxRow style={{height: 48}}>
        {metadata.checklistCreate
          ? <Button
            onClick={() => EditChecklistHelper.addChecklist(metadata.checklistCreator())}
            startIcon={<AddOutlined/>}>New list</Button>
          : null}
        {metadata.checklistEdit
          ? <Button
            disabled={loading}
            onClick={() => {
              setLoading(true);
              (async function () {
                const checklist = await metadata.checklistLoader(props.source.getValue(props.contentItem), metadata.checklistName);
                EditChecklistHelper.editChecklist(checklist);
                setLoading(false);
              })();
            }}
            startIcon={<AddOutlined/>}>Edit</Button>
          : null}
        <StyledSpan/>
        <Button
          onClick={() => {
            options.forEach(option => option.selected = true);
            setOptions([...options]);
          }}>
          Select All
        </Button>
      </StyledBoxRow>
      : null}
    {options.map((option, index) => <Button
      onClick={() => {
        option.selected = !option.selected;
        setOptions([...options.slice(0, index), option, ...options.slice(index + 1)]);
      }}
      style={{padding: 0, textAlign: "start"}}>
      <StyledBoxRow style={{width: '100%', padding: PD_LG}}>
        {option.selected
          ? <CheckBoxOutlined/>
          : <CheckBoxOutlineBlankOutlined/>}
        <Typography style={{flexGrow: 1}}>{option.item.text}</Typography>
      </StyledBoxRow>
    </Button>)}
  </StyledBoxColumn>;
}
